import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import type { ClockItem } from '../../../../transformers/zod-schemas';
import { useSound } from '../../../sounds';

export interface ClockRendererProps {
  item: ClockItem;
  value?: string;
  validationStatus?: 'correct' | 'incorrect' | null;
  onChange?: (widgetId: string, value: string) => void;
}

function parseTime(s: string): { h: number; m: number } {
  const parts = s.split(':');
  return { h: Number(parts[0]), m: Number(parts[1]) };
}

function formatTime(h: number, m: number): string {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatDisplay(h24: number, m: number, format: '12h' | '24h'): string {
  if (format === '24h') return formatTime(h24, m);
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
}

const CLOCK_SIZE = 160;
const INTERACTIVE_SCALE = 1.2;
const CENTER = CLOCK_SIZE / 2;
const FACE_RADIUS = CENTER - 6;
const HOUR_HAND = FACE_RADIUS * 0.45;
const MINUTE_HAND = FACE_RADIUS * 0.65;
const HOUR_LABELS = Array.from({ length: 12 }, (_, i) => i + 1);

// Arrow hand geometry — proportions match clock-icon.svg (pointing up = -Y before rotation)
// Minute: shaft=14/179, arrowW=28/179, tipH=50/179, tail=10/179 of hand length
const M_SHAFT = Math.round(MINUTE_HAND * 0.049);   // ≈2
const M_ARROW = Math.round(MINUTE_HAND * 0.156);   // ≈8
const M_TIP   = Math.round(MINUTE_HAND * 0.279);   // ≈13
const M_TAIL  = Math.round(MINUTE_HAND * 0.056);   // ≈3
// Hour: shaft=15/122, arrowW=30/122, tipH=45/122, tail=10/122 of hand length
const H_SHAFT = Math.round(HOUR_HAND * 0.074);     // ≈2
const H_ARROW = Math.round(HOUR_HAND * 0.246);     // ≈8
const H_TIP   = Math.round(HOUR_HAND * 0.369);     // ≈12
const H_TAIL  = Math.round(HOUR_HAND * 0.082);     // ≈3

export const ClockRenderer: React.FC<ClockRendererProps> = ({
  item,
  value,
  validationStatus,
  onChange,
}) => {
  const format = item.format ?? '24h';
  const interactive = item.mode === 'interactive';
  const snap = interactive && 'snap' in item ? (item.snap ?? 1) : 1;

  const initial = parseTime(interactive && value ? value : item.value);
  const [hours, setHours] = useState(initial.h);
  const [minutes, setMinutes] = useState(initial.m);

  useEffect(() => {
    if (interactive && value) {
      const t = parseTime(value);
      setHours(t.h);
      setMinutes(t.m);
    }
  }, [value, interactive]);

  const svgRef = useRef<SVGSVGElement>(null);
  const draggingRef = useRef<'hour' | 'minute' | null>(null);
  const playSound = useSound();

  const updateFromPointer = useCallback((clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg || !draggingRef.current) return;
    const rect = svg.getBoundingClientRect();
    const dx = clientX - rect.left - CENTER;
    const dy = clientY - rect.top - CENTER;
    // 12 o'clock = up = -Y; angle in [0, 2π)
    const angle = (Math.atan2(dx, -dy) + 2 * Math.PI) % (2 * Math.PI);

    if (draggingRef.current === 'minute') {
      const rawMin = (angle / (2 * Math.PI)) * 60;
      const snapped = (Math.round(rawMin / snap) * snap) % 60;
      // Tick only when crossing into a new snap step, not on every pointermove.
      if (snapped !== minutes) playSound('clock-minute-tick');
      setMinutes(snapped);
      if (item.mode === 'interactive' && onChange) {
        onChange(item.name, formatTime(hours, snapped));
      }
    } else {
      // Hour hand: full revolution = 12h. Use 24h internal — preserve AM/PM half.
      const rawHour12 = (angle / (2 * Math.PI)) * 12;
      const rounded = Math.round(rawHour12) % 12;
      const isPm = hours >= 12;
      const newH = (rounded === 0 ? 0 : rounded) + (isPm ? 12 : 0);
      const h24 = newH % 24;
      if (h24 !== hours) playSound('clock-hour-tick');
      setHours(h24);
      if (item.mode === 'interactive' && onChange) {
        onChange(item.name, formatTime(h24, minutes));
      }
    }
  }, [snap, hours, minutes, item.mode, item.name, onChange, playSound]);

  const onPointerDown = (hand: 'hour' | 'minute') => (e: React.PointerEvent<SVGElement>) => {
    if (!interactive) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    draggingRef.current = hand;
    updateFromPointer(e.clientX, e.clientY);
  };

  const onPointerMove = (e: React.PointerEvent<SVGElement>) => {
    if (!interactive || !draggingRef.current) return;
    updateFromPointer(e.clientX, e.clientY);
  };

  const onPointerUp = (e: React.PointerEvent<SVGElement>) => {
    if (!interactive) return;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    draggingRef.current = null;
  };

  const hourAngleRad = useMemo(() => {
    const h12 = hours % 12;
    return (((h12 + minutes / 60) / 12) * 2 * Math.PI);
  }, [hours, minutes]);

  const minuteAngleRad = useMemo(() => (minutes / 60) * 2 * Math.PI, [minutes]);

  const hourX = CENTER + Math.sin(hourAngleRad) * HOUR_HAND;
  const hourY = CENTER - Math.cos(hourAngleRad) * HOUR_HAND;
  const minuteX = CENTER + Math.sin(minuteAngleRad) * MINUTE_HAND;
  const minuteY = CENTER - Math.cos(minuteAngleRad) * MINUTE_HAND;

  const hourDeg = (hourAngleRad * 180) / Math.PI;
  const minuteDeg = (minuteAngleRad * 180) / Math.PI;

  const scale = interactive ? INTERACTIVE_SCALE : 1;
  const svgSize = CLOCK_SIZE * scale;
  const ringSize = FACE_RADIUS * 2 * scale;
  const svgOffset = -(CENTER - FACE_RADIUS) * scale;

  const statusClass = validationStatus ? `clock-${validationStatus}` : '';

  // ── Digital clock ────────────────────────────────────────────────────────
  if (item.clockType === 'digital') {
    return (
      <span className={`clock clock-digital ${statusClass}`} dir="ltr">
        <span className="clock-digital-wrapper">
          <svg className="clock-digital-tabs" width="85" height="9" viewBox="0 0 85 9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 8C0 3.58172 3.58172 0 8 0H14C18.4183 0 22 3.58172 22 8V9H0V8Z" fill="#AE9CFF"/>
            <path d="M63 8C63 3.58172 66.5817 0 71 0H77C81.4183 0 85 3.58172 85 8V9H63V8Z" fill="#AE9CFF"/>
          </svg>
          <span className="clock-digital-display">{formatDisplay(hours, minutes, format)}</span>
        </span>
      </span>
    );
  }

  // ── Analog clock ─────────────────────────────────────────────────────────

  return (
    <span className={`clock clock-analog ${interactive ? 'clock-interactive' : 'clock-display'} ${statusClass}`} dir="ltr">
      <span className="clock-face-ring" style={{ width: ringSize, height: ringSize }}>
        <svg
          ref={svgRef}
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${CLOCK_SIZE} ${CLOCK_SIZE}`}
          style={{ top: svgOffset, left: svgOffset }}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="clock-svg"
        >
        <circle
          cx={CENTER}
          cy={CENTER}
          r={FACE_RADIUS}
          className="clock-face"
        />
        {/* Hour ticks */}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * 2 * Math.PI;
          const x1 = CENTER + Math.sin(a) * (FACE_RADIUS - 8);
          const y1 = CENTER - Math.cos(a) * (FACE_RADIUS - 8);
          const x2 = CENTER + Math.sin(a) * FACE_RADIUS;
          const y2 = CENTER - Math.cos(a) * FACE_RADIUS;
          return <line key={`t${i}`} x1={x1} y1={y1} x2={x2} y2={y2} className="clock-tick clock-tick-hour" />;
        })}
        {/* Minute ticks */}
        {Array.from({ length: 60 }).map((_, i) => {
          if (i % 5 === 0) return null;
          const a = (i / 60) * 2 * Math.PI;
          const x1 = CENTER + Math.sin(a) * (FACE_RADIUS - 4);
          const y1 = CENTER - Math.cos(a) * (FACE_RADIUS - 4);
          const x2 = CENTER + Math.sin(a) * FACE_RADIUS;
          const y2 = CENTER - Math.cos(a) * FACE_RADIUS;
          return <line key={`m${i}`} x1={x1} y1={y1} x2={x2} y2={y2} className="clock-tick clock-tick-minute" />;
        })}
        {/* Hour number labels */}
        {HOUR_LABELS.map((n) => {
          const a = (n / 12) * 2 * Math.PI;
          const r = FACE_RADIUS - 20;
          const x = CENTER + Math.sin(a) * r;
          const y = CENTER - Math.cos(a) * r;
          return (
            <text key={`l${n}`} x={x} y={y} className="clock-number" textAnchor="middle" dominantBaseline="central">
              {n}
            </text>
          );
        })}
        {/* Hour arrow — single 7-point polygon, same shape as clock-icon.svg */}
        <polygon
          transform={`translate(${CENTER},${CENTER}) rotate(${hourDeg})`}
          points={[
            `0,${-HOUR_HAND}`,
            `${H_ARROW},${-(HOUR_HAND - H_TIP)}`,
            `${H_SHAFT},${-(HOUR_HAND - H_TIP)}`,
            `${H_SHAFT},${H_TAIL}`,
            `${-H_SHAFT},${H_TAIL}`,
            `${-H_SHAFT},${-(HOUR_HAND - H_TIP)}`,
            `${-H_ARROW},${-(HOUR_HAND - H_TIP)}`,
          ].join(' ')}
          className="clock-arrow clock-arrow-hour"
          style={{ pointerEvents: 'none' }}
        />
        {/* Minute arrow — single 7-point polygon */}
        <polygon
          transform={`translate(${CENTER},${CENTER}) rotate(${minuteDeg})`}
          points={[
            `0,${-MINUTE_HAND}`,
            `${M_ARROW},${-(MINUTE_HAND - M_TIP)}`,
            `${M_SHAFT},${-(MINUTE_HAND - M_TIP)}`,
            `${M_SHAFT},${M_TAIL}`,
            `${-M_SHAFT},${M_TAIL}`,
            `${-M_SHAFT},${-(MINUTE_HAND - M_TIP)}`,
            `${-M_ARROW},${-(MINUTE_HAND - M_TIP)}`,
          ].join(' ')}
          className="clock-arrow clock-arrow-minute"
          style={{ pointerEvents: 'none' }}
        />
        {/* Invisible hit-target lines — tests read x2/y2, interactive drag fires here */}
        <line
          x1={CENTER} y1={CENTER} x2={hourX} y2={hourY}
          className="clock-hand clock-hand-hour"
          stroke="transparent" strokeWidth={20}
          onPointerDown={onPointerDown('hour')}
        />
        <line
          x1={CENTER} y1={CENTER} x2={minuteX} y2={minuteY}
          className="clock-hand clock-hand-minute"
          stroke="transparent" strokeWidth={16}
          onPointerDown={onPointerDown('minute')}
        />
        {/* Center dot */}
        <circle cx={CENTER} cy={CENTER} r={4} className="clock-center" />
        </svg>
      </span>
      {format === '12h' && (
        <span className="clock-period-badge">{hours >= 12 ? 'PM' : 'AM'}</span>
      )}
    </span>
  );
};
